import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Check, Zap, CreditCard } from "lucide-react";
import Countdown from "./Countdown";
import AddToCalendar from "./AddToCalendar";
import { getRegistrationStatus } from "@/lib/api";
import { useSiteContent } from "@/lib/siteContent";

const buildIncludes = (durationLabel) => [
  `${durationLabel} live online build lab`,
  "Beginner-friendly AI & no-code instruction",
  "Real-time demonstration",
  "Practical prompts you can reuse",
  "Website & funnel examples",
  "App & dashboard overview",
  "Workflow & automation walkthrough",
  "Digital skill-building guidance",
  "Live Q&A",
  "Replay access (if applicable)",
];

export default function Offer({ courseDate }) {
  const [cap, setCap] = useState({ can_register: true, cta_label: "Save My Seat", message: "", remaining: null });
  const sc = useSiteContent();
  const INCLUDES = buildIncludes(sc.lab_duration_label);
  useEffect(() => { getRegistrationStatus().then(setCap).catch(() => {}); }, []);

  return (
    <section id="offer" data-testid="offer-section" className="relative py-24 lg:py-32 bg-bone border-y border-[var(--line)]">
      <div className="max-w-[1240px] mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="eyebrow mb-5">The Offer</div>
            <h2 className="font-display text-[44px] sm:text-5xl lg:text-[72px] leading-[1.02] tracking-[-0.035em] text-ink mb-6">
              One price. <br />
              Real <span className="font-serif-em italic text-brand font-light">output.</span>
            </h2>
            <p className="text-[17px] leading-relaxed text-ink/70 mb-2">
              Limited seats available. Once the class is full, registration will close.
            </p>
            <p className="text-[15px] leading-relaxed text-ink font-medium mb-4" data-testid="offer-intro-offer">
              {sc.intro_offer_line}
            </p>
            {cap.message && (
              <p className="text-[15px] text-ink font-medium mb-10" data-testid="offer-capacity-msg">{cap.message}</p>
            )}
            {!cap.message && <div className="mb-10" />}

            <div>
              <div className="eyebrow mb-3 !text-ink/70">Build Lab kicks off in</div>
              <Countdown targetIso={courseDate} light />
              {/* Big, bold headline date */}
              <div className="mt-6" data-testid="offer-date-block">
                <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-brand mb-1.5">
                  Next AI Build Lab
                </div>
                <div
                  className="font-black uppercase leading-[0.98] tracking-[-0.02em] text-ink"
                  style={{
                    fontSize: "clamp(30px, 3.6vw, 46px)",
                    fontFamily: "'Inter', -apple-system, 'Segoe UI', system-ui, sans-serif",
                  }}
                  data-testid="offer-lab-date"
                >
                  {sc.lab_date_primary_label}
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-2 text-[14px] text-ink/70">
                  <span className="font-bold text-ink">{sc.lab_date_primary_time}</span>
                  <span className="text-ink/30">·</span>
                  <span>Live online</span>
                  <AddToCalendar />
                </div>
              </div>
              {sc.lab_date_secondary_label && (
                <div className="text-xs text-[var(--muted)] mt-3" data-testid="offer-secondary-date">
                  Next after: <strong className="text-ink/85">{sc.lab_date_secondary_label}</strong>
                  {sc.lab_date_secondary_status ? ` · ${sc.lab_date_secondary_status}` : ""}
                </div>
              )}
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="bg-white border border-ink rounded-3xl p-8 lg:p-12 relative overflow-hidden shadow-[0_30px_80px_-30px_rgba(10,10,10,0.18)]">
              <div className="absolute top-7 right-7">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-brand-soft border border-brand/20">
                  <span className="w-1.5 h-1.5 rounded-full bg-brand animate-pulse" />
                  <span className="text-[11px] font-mono-spec uppercase tracking-wider text-brand font-medium">
                    {cap.can_register ? "Enrollment Open" : cap.cta_label}
                  </span>
                </div>
              </div>

              <div className="eyebrow mb-3">Course</div>
              <h3 className="font-display text-3xl sm:text-4xl text-ink tracking-[-0.03em] leading-tight">
                AI Website &amp; App Builder Course
              </h3>
              <p className="text-[var(--muted)] mt-2 font-serif-em italic text-lg font-light">
                A live {sc.lab_duration_label} online build lab.
              </p>

              <div className="hairline my-8" />

              <div className="flex items-baseline gap-3 mb-1">
                <div className="font-display text-6xl sm:text-7xl text-ink tracking-[-0.04em]" data-testid="offer-price">{sc.lab_price_label}</div>
                <div className="text-[var(--muted)]">one-time</div>
              </div>
              <div className="text-sm text-[var(--muted)]">All-in. No subscription, no upsells.</div>
              <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-brand/8 border border-brand/20 text-[13px] text-ink/85" data-testid="offer-klarna-afterpay">
                <CreditCard size={13} className="text-brand" /> {sc.klarna_afterpay_line}
              </div>

              <div className="mt-8 grid sm:grid-cols-2 gap-x-6 gap-y-3">
                {INCLUDES.map((it, i) => (
                  <div key={it} data-testid={`includes-${i}`} className="flex items-start gap-3 text-ink">
                    <Check size={16} className="text-brand shrink-0 mt-1" strokeWidth={2.5} />
                    <span className="text-[15px] leading-snug">{it}</span>
                  </div>
                ))}
              </div>

              <div className="mt-10">
                <a href={cap.can_register ? "#register" : "#offer"} data-testid="offer-primary-cta" data-cta="offer-primary">
                  <Button
                    disabled={!cap.can_register}
                    className="w-full h-14 rounded-full btn-ink font-medium text-base disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {cap.can_register && <Zap size={16} className="mr-2" />}
                    {cap.can_register ? `Save My Seat — ${sc.lab_price_label}` : cap.cta_label}
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
